import "../sentry.js";
import * as Sentry from "@sentry/react";

// Redirect all users to the new siakad system.
// For PWA users: this catches the first launch after deploy,
// before the updated service worker activates and takes over.
// Regular browser visits are already handled by Firebase Hosting redirects.
if (import.meta.env.PROD) {
    window.location.replace("https://siakad.ppgcikampek.id");
}

import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "./index.css";
import App from "./App.jsx";
import MaintenanceView from "./maintenance/pages/MaintenanceView.jsx";
import { useVersionCheck } from "./shared/hooks/useVersionCheck.js";
import NewModal from "./shared/Components/Modal/NewModal.jsx";
import PWAInstallPrompt from "./shared/Components/PWA/PWAInstallPrompt.jsx";
import { useMaintenanceFlag } from "./shared/hooks/useMaintenanceFlag.js";
import { useTestingFlag } from "./shared/hooks/useTestingFlag.js";

// Prevent automatic browser translation (Google Translate / Chrome)
if (typeof document !== "undefined") {
    document.documentElement.setAttribute("translate", "no");
    // Ensure the document language is set to Bahasa Indonesia
    document.documentElement.setAttribute("lang", "id");
    if (document.body) {
        document.body.classList.add("notranslate");
    } else {
        document.addEventListener("DOMContentLoaded", () => {
            document.body && document.body.classList.add("notranslate");
        });
    }
}

// PWA Install Detection
let deferredPrompt;
window.addEventListener("beforeinstallprompt", (e) => {
    console.log("PWA install prompt available");
    deferredPrompt = e;
});

window.addEventListener("appinstalled", (evt) => {
    console.log("PWA was installed");
});

// Buffer polyfill for libraries (eg. @react-pdf/renderer) that expect Node's Buffer
// Dynamically import the polyfill to avoid Vite externalizing `buffer` during static analysis.
if (typeof globalThis.Buffer === "undefined") {
    import("buffer")
        .then(({ Buffer: Buf }) => {
            globalThis.Buffer = Buf;
            // also set window for any libraries referencing window.Buffer
            try {
                window.Buffer = Buf;
            } catch (e) {
                // ignore on non-browser-like environments
            }
        })
        .catch(() => {
            // If import fails, leave it undefined — libraries should handle absence gracefully
        });
}

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnMount: "always",
            refetchOnWindowFocus: true,
            retry: 3,
        },
    },
});

const AppWrapper = () => {
    const { modalState, closeModal } = useVersionCheck();
    const {
        maintenance: isMaintenance,
        targetDate,
        loading,
    } = useMaintenanceFlag({
        defaultValue: false,
    });
    const { testing = false, loading: loadingTesting } = useTestingFlag({
        defaultValue: false,
    });

    console.log("Maintenance mode:", isMaintenance, "targetDate:", targetDate);

    useEffect(() => {
        if (screen.orientation && screen.orientation.lock) {
            screen.orientation.lock("portrait").catch((err) => {
                console.warn("Orientation lock failed:", err);
            });
        }
    }, []);

    // Optionally, you can render nothing or a lightweight splash while we read the flags
    if (loading || loadingTesting) return null;

    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                {/* Show testing banner when testing flag is true */}
                {testing && <></>}

                {isMaintenance ? (
                    <MaintenanceView targetDate={targetDate} />
                ) : (
                    <App />
                )}
                {/* <App /> */}
                <ReactQueryDevtools initialIsOpen={false} />
                <PWAInstallPrompt />
                <NewModal
                    modalState={modalState}
                    onClose={closeModal}
                    cancelText="Nanti"
                />
            </QueryClientProvider>
        </StrictMode>
    );
};

createRoot(document.getElementById("root"), {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
}).render(<AppWrapper />);
